<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Price Tier' : 'Add Price Tier'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-12 mb-3">
              <div class="alert alert-info fs-13 py-2">
                <i class="ri-information-line me-1"></i>
                Set the page range for this tier. Use <strong>Max Pages = empty</strong> to create an open-ended tier (e.g., "51+ pages"). Prices are automatically assigned to TRANSLATE orders based on the customer's submitted page count.
              </div>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Label</label>
              <input
                type="text"
                class="form-control"
                v-model="form.label"
                placeholder="e.g. 1 – 10 halaman"
              />
              <small class="text-muted">Friendly display name shown to users.</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4 d-flex align-items-center">
              <div class="form-check form-switch mt-3">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">Active (available for new orders)</label>
              </div>
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Min Pages <span class="text-danger">*</span></label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.min_pages"
                min="1"
                placeholder="1"
                required
              />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Max Pages</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.max_pages"
                :min="form.min_pages"
                placeholder="Leave empty for unlimited (∞)"
              />
              <small class="text-muted">Leave blank for open-ended tier.</small>
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Price (IDR) <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text bg-light text-muted">Rp</span>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="form.price"
                  min="0"
                  step="1000"
                  placeholder="150000"
                  required
                />
              </div>
              <small class="text-muted">Flat fee for orders in this page range.</small>
            </div>
          </div>

          <!-- Price Preview -->
          <div class="p-3 bg-light rounded-3 mb-4 d-flex align-items-center gap-3" v-if="form.price">
            <i class="ri-price-tag-3-line fs-20 text-primary"></i>
            <div>
              <div class="fs-13 text-muted">Preview</div>
              <div class="fw-semibold text-dark">
                {{ form.label || `${form.min_pages}–${form.max_pages ?? '∞'} halaman` }} →
                <span class="text-primary">{{ formatPrice(form.price) }}</span>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/translate-price-tiers" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Tier' : 'Create Tier' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import translatePriceTierService from '@/services/translatePriceTierService';

export default defineComponent({
  name: 'TranslatePriceTierFormPage',
  components: { PageTitle },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const isEditing = ref(false);
    const tierId = ref<number | null>(null);
    const loading = ref(false);

    const form = ref({
      label: '',
      min_pages: 1,
      max_pages: null as number | null,
      price: 0,
      is_active: true,
    });

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        tierId.value = Number(route.params.id);
        await fetchTier(tierId.value);
      }
    });

    const fetchTier = async (id: number) => {
      try {
        const response = await translatePriceTierService.getById(id);
        const data = response.data;
        form.value = {
          label: data.label || '',
          min_pages: data.min_pages,
          max_pages: data.max_pages ?? null,
          price: Number(data.price),
          is_active: !!data.is_active,
        };
      } catch (error) {
        console.error('Error fetching tier:', error);
        router.push('/translate-price-tiers');
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload = {
          ...form.value,
          max_pages: form.value.max_pages || null,
        };

        if (isEditing.value && tierId.value) {
          await translatePriceTierService.update(tierId.value, payload);
        } else {
          await translatePriceTierService.create(payload);
        }
        router.push('/translate-price-tiers');
      } catch (error: any) {
        console.error('Error saving tier:', error);
        alert(error.response?.data?.message || 'Failed to save tier.');
      } finally {
        loading.value = false;
      }
    };

    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(price);
    };

    return { isEditing, form, loading, submitForm, formatPrice };
  }
});
</script>
